import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from 'antd';
import cs from 'classnames';

import { AddPaymentMethodModal } from 'components/Payment/Modals/AddPaymentMethodModal/AddPaymentMethodModal';

import { selectIsDefaultPaymentMethodSet } from 'features/client/clientSelectors';
import { getClientPaymentMethodsInfo } from 'features/client/clientThunks';
import JobsService from 'features/jobs/jobsService';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch } from 'hooks/useTypedRedux';

import { GTMEventLogger } from 'utils/analytics';
import { notify } from 'utils/toast/createToast';

import { AlertType, CreateJobFlow, GTMEvents } from 'constants/enums';
import { CREATE_JOB } from 'constants/path';
import { DEBOUNCE_JOBS_TIMEOUT_MS } from 'constants/uiSettings';

import { ReactComponent as AddPlusIcon } from 'assets/icons/addSquarePlus.svg';

import styles from './CreateJobButton.module.scss';

interface CreateJobButtonProps {
  isFullWidth?: boolean;
  renderAddJobButton?: (onClick: () => void) => ReactNode;
}

const CreateJobButton: FC<CreateJobButtonProps> = ({
  isFullWidth,
  renderAddJobButton
}: CreateJobButtonProps) => {
  const { t } = useTranslation();
  const isDefaultPaymentMethodSet = useSelector(
    selectIsDefaultPaymentMethodSet
  );
  const [isPaymentMethodModalVisible, setIsPaymentMethodModalVisible] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const catchError = useErrorCatch();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleCreateClick = async () => {
    GTMEventLogger(GTMEvents.PaidJobCreateStart);

    try {
      setIsLoading(true);
      const freeJobsData = await JobsService.getFreeJobsInfo();

      const hasFreeJobs = freeJobsData.freeJobsLeft > 0;
      const canCreatePaidJob = isDefaultPaymentMethodSet;

      const createJobFlow = hasFreeJobs
        ? CreateJobFlow.Free
        : canCreatePaidJob
          ? CreateJobFlow.Paid
          : CreateJobFlow.PaymentRequired;

      switch (createJobFlow) {
        case CreateJobFlow.Free:
        case CreateJobFlow.Paid:
          navigate(CREATE_JOB);
          break;
        case CreateJobFlow.PaymentRequired:
          setIsPaymentMethodModalVisible(true);
          break;
      }
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.inviteJobseeker')
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPaymentMethodSuccess = () => {
    dispatch(getClientPaymentMethodsInfo());
    setIsPaymentMethodModalVisible(false);
    setIsLoading(false);
    notify({
      type: AlertType.Success,
      message: t('page.jobs.addPayment.success.message'),
      description: t('page.jobs.addPayment.success.description')
    });
    setTimeout(() => {
      navigate(CREATE_JOB);
    }, DEBOUNCE_JOBS_TIMEOUT_MS);
  };

  const handlePaymentMethodError = () => {
    setIsLoading(false);
  };

  const handleSubmitStart = () => {
    setIsLoading(true);
  };

  const handleModalCancel = () => {
    setIsPaymentMethodModalVisible(false);
  };

  return (
    <>
      {renderAddJobButton ? (
        renderAddJobButton(handleCreateClick)
      ) : (
        <Button
          type="primary"
          icon={<AddPlusIcon />}
          className={cs(styles.createJobButton, {
            [styles.fullWidth]: isFullWidth
          })}
          onClick={handleCreateClick}
          loading={isLoading}
        >
          {t('page.jobs.createAIJob')}
        </Button>
      )}

      <AddPaymentMethodModal
        open={isPaymentMethodModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleAddPaymentMethodSuccess}
        onError={handlePaymentMethodError}
        onSubmitStart={handleSubmitStart}
        loading={isLoading}
      />
    </>
  );
};

export default CreateJobButton;
